import { Layer } from '../core/Layer';
import { ImageAdjustmentValues } from '../types';

export class Adjustments {
  public static applyAdjustments(layer: Layer, values: ImageAdjustmentValues): void {
    const width = layer.width;
    const height = layer.height;
    const imgData = layer.ctx.getImageData(0, 0, width, height);
    const data = imgData.data;

    const b = values.brightness / 100; // -1 to 1
    const c = (values.contrast + 100) / 100; // 0 to 2
    const s = (values.saturation + 100) / 100; // 0 to 2
    const exp = Math.pow(2, values.exposure / 50); // exposure multiplier
    const temp = values.temperature / 100; // -1 to 1

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue; // skip transparent

      let r = data[i];
      let g = data[i + 1];
      let bVal = data[i + 2];

      // Exposure
      r = r * exp;
      g = g * exp;
      bVal = bVal * exp;

      // Brightness
      r += b * 255;
      g += b * 255;
      bVal += b * 255;

      // Contrast
      r = (r - 128) * c + 128;
      g = (g - 128) * c + 128;
      bVal = (bVal - 128) * c + 128;

      // Temperature (warm = more red, less blue; cool = more blue, less red)
      if (temp > 0) {
        r += temp * 30;
        bVal -= temp * 30;
      } else if (temp < 0) {
        r -= Math.abs(temp) * 30;
        bVal += Math.abs(temp) * 30;
      }

      // Saturation
      const gray = 0.2989 * r + 0.587 * g + 0.114 * bVal;
      r = gray + (r - gray) * s;
      g = gray + (g - gray) * s;
      bVal = gray + (bVal - gray) * s;

      // Grayscale
      if (values.grayscale) {
        r = gray;
        g = gray;
        bVal = gray;
      }

      // Sepia
      if (values.sepia) {
        const tr = 0.393 * r + 0.769 * g + 0.189 * bVal;
        const tg = 0.349 * r + 0.686 * g + 0.168 * bVal;
        const tb = 0.272 * r + 0.534 * g + 0.131 * bVal;
        r = tr;
        g = tg;
        bVal = tb;
      }

      // Invert
      if (values.invert) {
        r = 255 - r;
        g = 255 - g;
        bVal = 255 - bVal;
      }

      data[i] = Math.min(255, Math.max(0, r));
      data[i + 1] = Math.min(255, Math.max(0, g));
      data[i + 2] = Math.min(255, Math.max(0, bVal));
    }

    layer.ctx.putImageData(imgData, 0, 0);

    // Apply Blur or Sharpen via canvas filters or convolution if requested
    if (values.blur > 0) {
      this.applyGaussianBlur(layer, values.blur);
    } else if (values.sharpen > 0) {
      this.applySharpen(layer, values.sharpen / 100);
    }
  }

  public static applyGaussianBlur(layer: Layer, radius: number): void {
    if (radius <= 0) return;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = layer.width;
    tempCanvas.height = layer.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    tempCtx.filter = `blur(${radius}px)`;
    tempCtx.drawImage(layer.canvas, 0, 0);

    layer.clear();
    layer.ctx.drawImage(tempCanvas, 0, 0);
  }

  public static applySharpen(layer: Layer, amount: number = 0.5): void {
    const width = layer.width;
    const height = layer.height;
    const imgData = layer.ctx.getImageData(0, 0, width, height);
    const src = imgData.data;
    const output = layer.ctx.createImageData(width, height);
    const dst = output.data;

    // 3x3 sharpen kernel:
    //  0, -a,  0
    // -a, 1+4a, -a
    //  0, -a,  0
    const a = amount;
    const center = 1 + 4 * a;

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        for (let c = 0; c < 3; c++) {
          const up = ((y - 1) * width + x) * 4 + c;
          const down = ((y + 1) * width + x) * 4 + c;
          const left = (y * width + (x - 1)) * 4 + c;
          const right = (y * width + (x + 1)) * 4 + c;

          const val = src[idx + c] * center - a * (src[up] + src[down] + src[left] + src[right]);
          dst[idx + c] = Math.min(255, Math.max(0, val));
        }
        dst[idx + 3] = src[idx + 3];
      }
    }

    layer.ctx.putImageData(output, 0, 0);
  }
}
